import React, { FC, useState, useCallback, useEffect } from 'react';

import Button from 'components/Button';

import searchIcon from 'assets/img/search-icon.svg';
import dropdownArrowDark from 'assets/img/dropdown-arrow-dark.svg';

import capitalizeFirstLetter from 'utils/CapitalizeFirstLetter';

import { CourseListDTO, CourseTableListDTO } from 'hooks/useAutoApproval';

import {
  Container,
  ButtonAndModalWrapper,
  LayerTouchable,
  ModalContainer,
  ModalHeader,
  CheckboxItem,
  ModalFooter,
  ButtonsWrapper,
  AnimatedArrow,
  ConflictItem,
  ModalWrapper,
} from './styles';

export interface CheckboxModalProps {
  data: CourseListDTO[];
  handleSelectedCourses: (data: CourseListDTO[]) => void;
  isVisible: boolean;
  initialValue?: CourseTableListDTO[];
}

const CheckboxModal: FC<CheckboxModalProps> = ({
  data,
  handleSelectedCourses,
  isVisible,
  initialValue,
}) => {
  const [isModalOpen, toggleModal] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<CourseListDTO[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [isCoursesSelected, setIsCoursesSelected] = useState<boolean>(false);

  useEffect(() => {
    toggleModal(false);
  }, [isVisible]);

  useEffect(() => {
    if (initialValue && initialValue?.length > 0) {
      const initialSelectedItems = initialValue.filter((initialItem) =>
        data.some((dataItem) => dataItem.id === initialItem.cursoId),
      );

      setSelectedItems(() => {
        return initialSelectedItems.map((item) => ({
          id: item.cursoId,
          conflito: false,
          curso: item.nome,
          nomeCampus: item.campus,
          turno: item.turno,
          mensalidade: item.mensalidade
        }));
      });
    }
  }, [data]);

  const handleFilterInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setFilter(value);
  };

  const filteredData = useCallback((): CourseListDTO[] => {
    const newData = data.filter((item) =>
      item.curso.toLowerCase().includes(filter.toLowerCase()),
    );

    return newData;
  }, [filter, data]);

  const handleCheckboxToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
    checkboxItem?: CourseListDTO,
  ): void => {
    const { checked } = event.target;

    if (!checkboxItem) {
      if (checked) {
        setSelectedItems(data);
      } else {
        setSelectedItems([]);
      }
      return;
    }

    if (checked) {
      setSelectedItems((currArray) => [...currArray, checkboxItem]);
    } else {
      setSelectedItems((currArray): CourseListDTO[] => {
        const newArray = currArray?.filter(
          (currItem) => currItem.id !== checkboxItem.id,
        );

        return newArray;
      });
    }
  };

  const clearCheckboxes = (): void => {
    setSelectedItems([]);
    setFilter('');
  };

  const handleSetSelectedCourses = () => {
    if (selectedItems.length <= 0) {
      alert('Você precisa selecionar pelo menos 1 curso');
      return;
    }

    handleSelectedCourses(selectedItems);

    setIsCoursesSelected(true);
    toggleModal(false);
    setFilter('');
  };

  useEffect(() => {
    setFilter('');
  }, [isModalOpen]);

  return (
    <>
      {isModalOpen && (
        <LayerTouchable onClick={(): void => toggleModal(!isModalOpen)} />
      )}
      <ButtonAndModalWrapper>
        <Container onClick={() => toggleModal(!isModalOpen)}>
          <div>
            <span>Cursos:</span>
            <span className="course-highlighted">
              {
                // eslint-disable-next-line no-nested-ternary
                selectedItems.length <= 0
                  ? 'Selecione'
                  : selectedItems.length === 1
                  ? `${selectedItems.length} curso selecionado`
                  : `${selectedItems.length} cursos selecionados`
              }
            </span>
          </div>
          <AnimatedArrow
            state={isModalOpen}
            src={dropdownArrowDark}
            alt="dropdown-arrow"
          />
        </Container>

        {isModalOpen && isVisible && (
          <ModalContainer isModalOpen={isModalOpen}>
            <ModalHeader>
              <input
                type="text"
                placeholder="Buscar cursos"
                value={filter}
                onChange={handleFilterInput}
              />
              <img src={searchIcon} alt="Search icon" />
            </ModalHeader>
            <ModalWrapper>
              {!filter && (
                <CheckboxItem isDefault>
                  <input
                    type="checkbox"
                    id="checkboxDefault"
                    onChange={(e) => handleCheckboxToggle(e)}
                    checked={data.length === selectedItems.length}
                  />
                  <label htmlFor="checkboxDefault">
                    Selecionar todos os cursos
                  </label>
                </CheckboxItem>
              )}

              {filteredData().map((item, index: number) =>
                item.conflito || !item.mensalidade ? (
                  <ConflictItem
                    key={item.id}
                    isPreviousInConflict={
                      filteredData[index === 0 ? 0 : index - 1]?.conflito
                    }
                  >
                    <label className="conflict-course__title">
                      {capitalizeFirstLetter(item.curso || '-')}
                      <span>
                        {item.turno || ''}
                        {item.turno && item.nomeCampus ? ' - ' : ''}
                        {item.nomeCampus || ''}
                      </span>
                    </label>
                    <p className="conflict-description">
                      {item.conflito ? "Esse curso já está previsto num mesmo período em outra automação" : "Curso sem mensalidade cadastrada, procure seu consultor"}
                    </p>
                  </ConflictItem>
                ) : (
                  <CheckboxItem key={`${item.id}`}>
                    <input
                      type="checkbox"
                      id={`checkboxItem-${item.id}`}
                      onChange={(e) => handleCheckboxToggle(e, item)}
                      checked={selectedItems.some(
                        (selectedItem) => selectedItem.id === item.id,
                      )}
                    />
                    <label htmlFor={`checkboxItem-${item.id}`}>
                      {capitalizeFirstLetter(item.curso || '-')}
                      <span>
                        {item.turno || ''}
                        {item.turno && item.nomeCampus ? ' - ' : ''}
                        {item.nomeCampus || ''}
                      </span>
                    </label>
                  </CheckboxItem>
                ),
              )}
            </ModalWrapper>
            <ModalFooter>
              <ButtonsWrapper>
                <Button
                  title="Limpar"
                  backgroundColor="#ffffff"
                  textColor="#98A2B2"
                  onClick={(): void => clearCheckboxes()}
                />
                <Button
                  title="Selecionar"
                  onClick={() => handleSetSelectedCourses()}
                />
              </ButtonsWrapper>
            </ModalFooter>
          </ModalContainer>
        )}
      </ButtonAndModalWrapper>
    </>
  );
};

export default CheckboxModal;
